import React, { useState } from 'react';
import styled from 'styled-components';

interface Props {
  title: string;
  notes?: string;
  images: string[];
  showImageIndexPill?: boolean;
  loading: boolean;
  onClose: () => void;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  background: var(--surface);
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 8px;
  padding: 1rem;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
`;

const CarouselWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1rem;
`;

const CarouselImage = styled.img`
  max-width: 100%;
  max-height: 560px;
  width: min(920px, 82vw);
  border-radius: 6px;
  object-fit: contain;
`;

const CarouselImageWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const ImageIndexPill = styled.span`
  position: absolute;
  top: 0.55rem;
  left: 0.55rem;
  min-width: 1.6rem;
  height: 1.6rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  background: rgba(0, 0, 0, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.2);
  pointer-events: none;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const NavBtn = styled.button`
  padding: 0.25rem 0.75rem;
  background: var(--primary);
  color: var(--onPrimary);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Notes = styled.pre`
  white-space: pre-wrap;
  font-family: inherit;
  background: var(--surfaceVariant);
  padding: 0.75rem;
  border-radius: 6px;
`;

export const SermonNotesModal: React.FC<Props> = ({
  title,
  notes,
  images,
  showImageIndexPill = false,
  loading,
  onClose,
}) => {
  const [idx, setIdx] = useState(0);
  const total = images.length;

  const prev = () => setIdx((i) => (i === 0 ? total - 1 : i - 1));
  const next = () => setIdx((i) => (i === total - 1 ? 0 : i + 1));

  return (
    <Overlay onClick={onClose}>
      <ModalWrapper onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        {loading ? (
          <p>Loading…</p>
        ) : (
          <>
            {title && <h3>{title}</h3>}
            {notes && <Notes>{notes}</Notes>}
            {total > 0 && (
              <CarouselWrapper>
                <CarouselImageWrap>
                  {showImageIndexPill && <ImageIndexPill>{idx + 1}</ImageIndexPill>}
                  <CarouselImage src={images[idx]} alt={`Slide ${idx + 1}`} />
                </CarouselImageWrap>
                {total > 1 && (
                  <NavButtons>
                    <NavBtn onClick={prev}>Prev</NavBtn>
                    <span>
                      {idx + 1} / {total}
                    </span>
                    <NavBtn onClick={next}>Next</NavBtn>
                  </NavButtons>
                )}
              </CarouselWrapper>
            )}
            {!notes && total === 0 && <p>No notes or images available.</p>}
          </>
        )}
      </ModalWrapper>
    </Overlay>
  );
};

export default SermonNotesModal;
